import React, { useEffect, useMemo, useState } from 'react';
import { CheckCircle2, ChevronDown, ChevronRight, RefreshCw, XCircle } from 'lucide-react';
import { toast } from 'sonner';
import api from '../../services/api';
import { transactionsService } from '../../services/transactions';
import { userSettingsService } from '../../services/userSettings';
import { getCurrentUser, hasPermission } from '../../utils/roles';

const TARGETS_EXPANDED_KEY = 'targets.expanded';
const DESK_GOAL_EDIT_ROLES = ['owner', 'admin', 'crm_manager'];

export const PENDING_BADGE_CLASS = 'bg-yellow-500/20 text-yellow-400';

const DEPT_ROLES = {
  sales: new Set(['head_of_sales', 'sales_team', 'sales_agent']),
  retention: new Set(['head_of_ret', 'ret_team', 'ret_agent']),
};

const MANAGE_STATUS_ROLES = new Set(['owner', 'admin', 'crm_manager', 'head_of_sales', 'head_of_ret']);

export const MONEY_ACCESS_ROLES = MANAGE_STATUS_ROLES;

const moneyFmt = (value) => `$${(Number(value) || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const transactionTypeClass = (type, isDark) => {
  if (type === 'withdrawal') return isDark ? 'text-red-400' : 'text-red-600';
  return isDark ? 'text-green-400' : 'text-green-600';
};

const transactionStatusClass = (status, isDark) => {
  if (status === 'pending') return 'text-yellow-400';
  return isDark ? 'text-gray-300' : 'text-gray-700';
};

export function Segmented({ value, onChange, items, isDark }) {
  return (
    <div className={`inline-flex h-10 w-fit rounded-lg border p-1 ${isDark ? 'border-gray-800 bg-[#0a0e1a]' : 'border-gray-200 bg-gray-50'}`}>
      {items.map(item => (
        <button
          key={item.key}
          type="button"
          onClick={() => onChange(item.key)}
          className={`inline-flex items-center gap-2 rounded-md px-4 text-xs font-semibold transition-colors ${
            value === item.key
              ? 'bg-purple-600 text-white shadow-sm'
              : isDark ? 'text-gray-400 hover:text-gray-200' : 'text-gray-500 hover:text-gray-800'
          }`}
        >
          <span>{item.label}</span>
          {item.badge !== undefined && (
            <span className={`min-w-5 rounded-full px-1.5 py-0.5 text-[10px] font-bold leading-none ${
              value === item.key
                ? 'bg-white/20 text-white'
                : PENDING_BADGE_CLASS
            }`}>
              {item.badge}
            </span>
          )}
        </button>
      ))}
    </div>
  );
}

export function useMoneyPendingCounts() {
  const [pendingCounts, setPendingCounts] = useState({ sales: 0, retention: 0, total: 0 });

  const loadPendingCounts = async () => {
    const [sales, retention] = await Promise.all([
      transactionsService.getTransactions(1, 1, { department: 'sales', status: 'pending' }).catch(() => ({ total: 0 })),
      transactionsService.getTransactions(1, 1, { department: 'retention', status: 'pending' }).catch(() => ({ total: 0 })),
    ]);
    const nextCounts = {
      sales: sales.total || 0,
      retention: retention.total || 0,
      total: (sales.total || 0) + (retention.total || 0),
    };
    setPendingCounts(nextCounts);
    window.dispatchEvent(new CustomEvent('money-pending-count-changed', { detail: nextCounts }));
  };

  useEffect(() => { loadPendingCounts(); }, []);

  return { pendingCounts, loadPendingCounts };
}

export function MoneyApprovalPanel({ department, isDark, pendingCount, onPendingCountsChanged }) {
  const currentUser = useMemo(getCurrentUser, []);
  const canManage = hasPermission(currentUser, 'View Finance');
  const [status, setStatus] = useState('pending');
  const [rows, setRows] = useState([]);
  const [amountDrafts, setAmountDrafts] = useState({});
  const [loading, setLoading] = useState(false);
  const [savingId, setSavingId] = useState(null);
  const [savingAmountId, setSavingAmountId] = useState(null);

  const loadRows = async () => {
    setLoading(true);
    try {
      const data = await transactionsService.getTransactions(1, 100, {
        department,
        status: status === 'all' ? '' : status,
      });
      const nextRows = data.transactions || [];
      setRows(nextRows);
      setAmountDrafts(nextRows.reduce((acc, row) => ({ ...acc, [row.id]: String(row.amount ?? 0) }), {}));
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load money');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadRows(); }, [department, status]);

  const saveAmount = async (txn, { silent = false } = {}) => {
    const draft = amountDrafts[txn.id] ?? txn.amount;
    const nextAmount = Number(draft);
    if (!Number.isFinite(nextAmount) || nextAmount <= 0) {
      toast.error('Amount must be greater than 0');
      throw new Error('Invalid amount');
    }
    if (Number(txn.amount) === nextAmount) return txn;

    setSavingAmountId(txn.id);
    try {
      const updated = await transactionsService.updateTransactionAmount(txn.id, nextAmount);
      setRows(prev => prev.map(row => row.id === txn.id ? updated : row));
      setAmountDrafts(prev => ({ ...prev, [txn.id]: String(updated.amount ?? 0) }));
      if (!silent) toast.success('Amount saved');
      return updated;
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to save amount');
      throw e;
    } finally {
      setSavingAmountId(null);
    }
  };

  const changeStatus = async (txn, nextStatus) => {
    setSavingId(txn.id);
    try {
      const txnToUpdate = nextStatus === 'completed' && txn.status === 'pending'
        ? await saveAmount(txn, { silent: true })
        : txn;
      const updated = await transactionsService.updateTransactionStatus(txnToUpdate.id, nextStatus);
      setRows(prev => (
        status === 'all' || updated.status === status
          ? prev.map(row => row.id === txn.id ? updated : row)
          : prev.filter(row => row.id !== txn.id)
      ));
      setAmountDrafts(prev => ({ ...prev, [updated.id]: String(updated.amount ?? 0) }));
      onPendingCountsChanged?.();
      toast.success(`Status changed to ${nextStatus}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to change status');
    } finally {
      setSavingId(null);
    }
  };

  const th = `px-4 py-3 text-left text-xs font-medium uppercase ${isDark ? 'text-gray-400 bg-[#0a0e1a]' : 'text-gray-500 bg-gray-50'}`;
  const td = `px-4 py-3 text-sm ${isDark ? 'text-gray-300' : 'text-gray-700'}`;
  const amountInput = `h-9 w-32 rounded-md border px-3 text-sm font-semibold tabular-nums focus:outline-none focus:ring-2 focus:ring-purple-500 disabled:opacity-60 ${isDark ? 'border-gray-700 bg-[#0a0e1a]' : 'border-gray-300 bg-white'}`;

  return (
    <div className="space-y-4">
      <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
        <h2 className={`text-lg font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>Money</h2>
        <div className="flex flex-wrap items-center gap-2">
          <Segmented
            value={status}
            onChange={setStatus}
            isDark={isDark}
            items={[
              { key: 'pending', label: 'Pending', badge: pendingCount },
              { key: 'completed', label: 'Completed' },
              { key: 'rejected', label: 'Rejected' },
              { key: 'all', label: 'All' },
            ]}
          />
          <button
            type="button"
            onClick={loadRows}
            className={`inline-flex h-10 items-center gap-2 rounded-lg border px-3 text-sm transition-colors ${isDark ? 'border-gray-800 text-gray-300 hover:bg-[#1a1f3a]' : 'border-gray-200 text-gray-700 hover:bg-gray-50'}`}
          >
            <RefreshCw size={15} /> Refresh
          </button>
        </div>
      </div>

      <div className={`rounded-lg border overflow-hidden ${isDark ? 'border-gray-800 bg-[#111827]' : 'border-gray-200 bg-white'}`}>
        <div className="overflow-x-auto">
          <table className="w-full min-w-[980px]">
            <thead>
              <tr>
                {['Date', 'Client', 'Agent', 'Role', 'Type', 'Amount', 'Status', 'Description', 'Actions'].map(h => (
                  <th key={h} className={th}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody className={`divide-y ${isDark ? 'divide-gray-800' : 'divide-gray-100'}`}>
              {loading ? (
                <tr><td colSpan={9} className={`${td} py-10 text-center`}>Loading...</td></tr>
              ) : rows.length === 0 ? (
                <tr><td colSpan={9} className={`${td} py-10 text-center ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>No transactions</td></tr>
              ) : rows.map(row => (
                <tr key={row.id} className={isDark ? 'hover:bg-[#1a1f3a]' : 'hover:bg-gray-50'}>
                  <td className={td}>{new Date(row.createdAt).toLocaleString()}</td>
                  <td className={`${td} font-medium`}>#{row.clientId}</td>
                  <td className={`${td} font-medium ${isDark ? 'text-white' : 'text-gray-900'}`}>{row.agentName || '-'}</td>
                  <td className={td}>{row.agentRole || '-'}</td>
                  <td className={`${td} capitalize font-medium ${transactionTypeClass(row.type, isDark)}`}>{row.type}</td>
                  <td className={td}>
                    {canManage && row.status === 'pending' ? (
                      <input
                        type="number"
                        min="0"
                        step="0.01"
                        value={amountDrafts[row.id] ?? row.amount}
                        disabled={savingId === row.id || savingAmountId === row.id}
                        onChange={e => setAmountDrafts(prev => ({ ...prev, [row.id]: e.target.value }))}
                        onBlur={() => saveAmount(row).catch(() => {})}
                        onKeyDown={e => {
                          if (e.key === 'Enter') e.currentTarget.blur();
                          if (e.key === 'Escape') setAmountDrafts(prev => ({ ...prev, [row.id]: String(row.amount ?? 0) }));
                        }}
                        className={`${amountInput} ${transactionTypeClass(row.type, isDark)}`}
                      />
                    ) : (
                      <span className={`font-semibold tabular-nums ${transactionTypeClass(row.type, isDark)}`}>{moneyFmt(row.amount)}</span>
                    )}
                  </td>
                  <td className={`${td} lowercase font-medium ${transactionStatusClass(row.status, isDark)}`}>{row.status}</td>
                  <td className={`${td} max-w-[220px] truncate`}>{row.description || '-'}</td>
                  <td className={td}>
                    {canManage ? (
                      <div className="flex items-center gap-2">
                        <button
                          type="button"
                          title="Complete"
                          disabled={savingId === row.id || savingAmountId === row.id || row.status === 'completed'}
                          onMouseDown={e => e.preventDefault()}
                          onClick={() => changeStatus(row, 'completed')}
                          className="rounded-md p-1.5 text-emerald-400 transition-colors hover:bg-emerald-500/10 disabled:opacity-35"
                        >
                          <CheckCircle2 size={16} />
                        </button>
                        <button
                          type="button"
                          title="Reject"
                          disabled={savingId === row.id || savingAmountId === row.id || row.status === 'rejected'}
                          onMouseDown={e => e.preventDefault()}
                          onClick={() => changeStatus(row, 'rejected')}
                          className="rounded-md p-1.5 text-red-400 transition-colors hover:bg-red-500/10 disabled:opacity-35"
                        >
                          <XCircle size={16} />
                        </button>
                      </div>
                    ) : (
                      <span className={isDark ? 'text-gray-600' : 'text-gray-400'}>-</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

export function TargetsPanel({ department, isDark }) {
  const [users, setUsers] = useState([]);
  const [targets, setTargets] = useState({});
  const [loading, setLoading] = useState(false);
  const [savingId, setSavingId] = useState(null);
  // Groups are collapsed by default, so we persist the set of EXPANDED keys
  // (per department). New desks/teams then stay collapsed until opened.
  const [expandedByDept, setExpandedByDept] = useState({ sales: [], retention: [] });

  const canEditDeskGoal = useMemo(() => DESK_GOAL_EDIT_ROLES.includes(getCurrentUser()?.role), []);

  useEffect(() => {
    userSettingsService.get(TARGETS_EXPANDED_KEY, {}).then(val => {
      setExpandedByDept({ sales: val?.sales || [], retention: val?.retention || [] });
    }).catch(() => {});
  }, []);

  const loadTargets = async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/users', { params: { limit: 500, active: 'true' } });
      const nextUsers = data.users || [];
      setUsers(nextUsers);
      setTargets(nextUsers.reduce((acc, user) => ({
        ...acc,
        [user.id]: {
          salesTargetFtd: user.salesTargetFtd ?? 0,
          retTargetPv: user.retTargetPv ?? 0,
        },
      }), {}));
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load targets');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadTargets(); }, []);

  const field = department === 'sales' ? 'salesTargetFtd' : 'retTargetPv';
  const label = department === 'sales' ? 'FTD target' : 'Target $';
  const headRole = department === 'sales' ? 'head_of_sales' : 'head_of_ret';
  const targetFmt = (value) => {
    const numeric = Number(value) || 0;
    if (department === 'sales') return numeric.toLocaleString('en-US', { maximumFractionDigits: 0 });
    return `$${numeric.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  const headRows = useMemo(() => {
    return users.filter(user => user.role === headRole);
  }, [headRole, users]);

  const headTargetTotal = useMemo(() => {
    return headRows.reduce((sum, user) => sum + Number(targets[user.id]?.[field] || 0), 0);
  }, [field, headRows, targets]);

  const rows = useMemo(() => {
    const roles = DEPT_ROLES[department];
    return users.filter(user => {
      const userDepartment = String(user.department || '').trim().toLowerCase();
      if (user.role === headRole) return false;
      return roles.has(user.role) || userDepartment === department;
    });
  }, [department, headRole, users]);

  const groupedTargets = useMemo(() => {
    const desks = new Map();
    rows.forEach(user => {
      const deskName = user.desk || 'No desk';
      const teamName = user.team || 'No team';
      const value = Number(targets[user.id]?.[field] || 0);
      if (!desks.has(deskName)) {
        desks.set(deskName, { name: deskName, total: 0, usersCount: 0, teams: new Map() });
      }
      const desk = desks.get(deskName);
      if (!desk.teams.has(teamName)) {
        desk.teams.set(teamName, { name: teamName, total: 0, usersCount: 0, users: [] });
      }
      const team = desk.teams.get(teamName);
      desk.total += value;
      desk.usersCount += 1;
      team.total += value;
      team.usersCount += 1;
      team.users.push(user);
    });

    const deskGroups = Array.from(desks.values())
      .map(desk => ({
        ...desk,
        teams: Array.from(desk.teams.values()).sort((a, b) => a.name.localeCompare(b.name)),
      }))
      .sort((a, b) => a.name.localeCompare(b.name));

    return {
      desks: deskGroups,
      total: deskGroups.reduce((sum, desk) => sum + desk.total, 0),
    };
  }, [field, rows, targets]);

  const deskKey = (desk) => `desk:${desk}`;
  const teamKey = (desk, team) => `team:${desk}::${team}`;

  const expandedKeys = useMemo(() => new Set(expandedByDept[department] || []), [expandedByDept, department]);

  const allKeys = useMemo(() => {
    const keys = [];
    groupedTargets.desks.forEach(desk => {
      keys.push(deskKey(desk.name));
      desk.teams.forEach(team => keys.push(teamKey(desk.name, team.name)));
    });
    return keys;
  }, [groupedTargets]);

  const allExpanded = allKeys.length > 0 && allKeys.every(key => expandedKeys.has(key));

  const persistExpanded = (nextForDept) => {
    const next = { ...expandedByDept, [department]: nextForDept };
    setExpandedByDept(next);
    userSettingsService.set(TARGETS_EXPANDED_KEY, next).catch(() => {});
  };

  const toggleKey = (key) => {
    const current = new Set(expandedByDept[department] || []);
    if (current.has(key)) current.delete(key); else current.add(key);
    persistExpanded(Array.from(current));
  };

  const toggleAll = () => persistExpanded(allExpanded ? [] : allKeys);

  // Soft reconciliation: distributed (sum cascaded to agents) vs the desk goal
  // (heads' target). Shown, never enforced.
  const distributed = groupedTargets.total;
  const deskGoal = headTargetTotal;
  const delta = distributed - deskGoal;
  const reconTone = deskGoal === 0 ? 'gray' : (delta === 0 ? 'green' : 'amber');
  const reconLabel = deskGoal === 0
    ? 'Desk goal not set'
    : (delta === 0
        ? 'Matches desk goal'
        : (delta > 0 ? `Over goal by ${targetFmt(delta)}` : `Under goal by ${targetFmt(-delta)}`));
  const reconBadge = {
    green: isDark ? 'bg-green-500/15 text-green-400' : 'bg-green-50 text-green-700',
    amber: isDark ? 'bg-yellow-500/15 text-yellow-400' : 'bg-yellow-50 text-yellow-700',
    gray: isDark ? 'bg-gray-500/15 text-gray-400' : 'bg-gray-100 text-gray-600',
  }[reconTone];

  const saveTarget = async (user) => {
    setSavingId(user.id);
    try {
      const payload = { [field]: Number(targets[user.id]?.[field] || 0) };
      const { data } = await api.patch(`/users/${user.id}/targets`, payload);
      setUsers(prev => prev.map(row => row.id === user.id ? { ...row, ...data } : row));
      setTargets(prev => ({ ...prev, [user.id]: { salesTargetFtd: data.salesTargetFtd ?? 0, retTargetPv: data.retTargetPv ?? 0 } }));
      toast.success('Target saved');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to save target');
    } finally {
      setSavingId(null);
    }
  };

  const th = `px-4 py-3 text-left text-xs font-medium uppercase ${isDark ? 'text-gray-400 bg-[#0a0e1a]' : 'text-gray-500 bg-gray-50'}`;
  const td = `px-4 py-3 text-sm ${isDark ? 'text-gray-300' : 'text-gray-700'}`;
  const groupTd = `px-4 py-3 text-sm font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`;
  const input = `w-32 rounded-md border px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-purple-500 ${isDark ? 'bg-[#0a0e1a] border-gray-700 text-white' : 'bg-white border-gray-300 text-gray-900'}`;
  const button = `rounded-md border px-3 py-2 text-sm transition-colors disabled:opacity-50 ${isDark ? 'border-gray-700 text-gray-300 hover:bg-[#2a2f4a]' : 'border-gray-300 text-gray-700 hover:bg-gray-50'}`;

  return (
    <div className="space-y-4">
      <div className="flex flex-col gap-3 lg:flex-row lg:items-end lg:justify-between">
        <div>
          <h2 className={`text-lg font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>Targets</h2>
          <p className={`mt-1 text-sm ${isDark ? 'text-gray-500' : 'text-gray-500'}`}>Monthly targets used in Result.</p>
        </div>
        <div className={`rounded-lg border px-4 py-3 ${isDark ? 'border-gray-800 bg-[#111827]' : 'border-gray-200 bg-white'}`}>
          <div className="flex items-end gap-4">
            <div>
              <div className={`text-xs uppercase ${isDark ? 'text-gray-500' : 'text-gray-500'}`}>Distributed</div>
              <div className={`mt-1 text-xl font-semibold tabular-nums ${isDark ? 'text-white' : 'text-gray-900'}`}>{targetFmt(distributed)}</div>
            </div>
            <div className={`pb-1 text-lg ${isDark ? 'text-gray-600' : 'text-gray-300'}`}>/</div>
            <div>
              <div className={`text-xs uppercase ${isDark ? 'text-gray-500' : 'text-gray-500'}`}>Desk goal</div>
              <div className={`mt-1 text-xl font-semibold tabular-nums ${isDark ? 'text-white' : 'text-gray-900'}`}>{targetFmt(deskGoal)}</div>
            </div>
          </div>
          <div className={`mt-2 inline-flex items-center rounded-md px-2 py-0.5 text-xs font-medium ${reconBadge}`}>
            {reconLabel}
          </div>
        </div>
      </div>

      <div className={`rounded-lg border overflow-hidden ${isDark ? 'border-gray-800 bg-[#111827]' : 'border-gray-200 bg-white'}`}>
        <div className="overflow-x-auto">
          <table className="w-full min-w-[780px]">
            <thead>
              <tr>
                {['Head', 'Role', 'Desk', 'Team', label, 'Actions'].map(h => <th key={h} className={th}>{h}</th>)}
              </tr>
            </thead>
            <tbody className={`divide-y ${isDark ? 'divide-gray-800' : 'divide-gray-100'}`}>
              {loading ? (
                <tr><td colSpan={6} className={`${td} py-6 text-center`}>Loading...</td></tr>
              ) : headRows.length === 0 ? (
                <tr><td colSpan={6} className={`${td} py-6 text-center ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>No head user</td></tr>
              ) : headRows.map(user => (
                <tr key={user.id} className={isDark ? 'hover:bg-[#1a1f3a]' : 'hover:bg-gray-50'}>
                  <td className={`${td} font-medium ${isDark ? 'text-white' : 'text-gray-900'}`}>{user.name}</td>
                  <td className={td}>{user.role}</td>
                  <td className={td}>{user.desk || '-'}</td>
                  <td className={td}>{user.team || '-'}</td>
                  <td className={td}>
                    {canEditDeskGoal ? (
                      <input
                        type="number"
                        min="0"
                        step={department === 'sales' ? '1' : '100'}
                        value={targets[user.id]?.[field] ?? 0}
                        onChange={e => setTargets(prev => ({
                          ...prev,
                          [user.id]: { ...(prev[user.id] || {}), [field]: e.target.value },
                        }))}
                        className={input}
                      />
                    ) : (
                      <span className="tabular-nums">{targetFmt(targets[user.id]?.[field] ?? 0)}</span>
                    )}
                  </td>
                  <td className={td}>
                    {canEditDeskGoal ? (
                      <button type="button" onClick={() => saveTarget(user)} disabled={savingId === user.id} className={button}>
                        {savingId === user.id ? 'Saving...' : 'Save'}
                      </button>
                    ) : null}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <div className={`rounded-lg border overflow-hidden ${isDark ? 'border-gray-800 bg-[#111827]' : 'border-gray-200 bg-white'}`}>
        <div className={`flex items-center justify-end border-b px-4 py-2 ${isDark ? 'border-gray-800' : 'border-gray-200'}`}>
          <button
            type="button"
            onClick={toggleAll}
            disabled={allKeys.length === 0}
            className={`text-xs font-medium disabled:opacity-40 ${isDark ? 'text-gray-400 hover:text-white' : 'text-gray-500 hover:text-gray-800'}`}
          >
            {allExpanded ? 'Collapse all' : 'Expand all'}
          </button>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full min-w-[780px]">
            <thead>
              <tr>
                {['Name', 'Role', 'Desk', 'Team', label, 'Actions'].map(h => <th key={h} className={th}>{h}</th>)}
              </tr>
            </thead>
            <tbody className={`divide-y ${isDark ? 'divide-gray-800' : 'divide-gray-100'}`}>
              {loading ? (
                <tr><td colSpan={6} className={`${td} py-10 text-center`}>Loading...</td></tr>
              ) : rows.length === 0 ? (
                <tr><td colSpan={6} className={`${td} py-10 text-center ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>No users</td></tr>
              ) : groupedTargets.desks.map(desk => {
                const dKey = deskKey(desk.name);
                const deskOpen = expandedKeys.has(dKey);
                return (
                <React.Fragment key={desk.name}>
                  <tr onClick={() => toggleKey(dKey)} className={`cursor-pointer ${isDark ? 'bg-[#0a0e1a] hover:bg-[#12182b]' : 'bg-gray-50 hover:bg-gray-100'}`}>
                    <td className={groupTd} colSpan={4}>
                      <span className="inline-flex items-center gap-1.5">
                        {deskOpen ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                        Desk: {desk.name}
                      </span>
                    </td>
                    <td className={`${groupTd} tabular-nums`}>{targetFmt(desk.total)}</td>
                    <td className={td}></td>
                  </tr>
                  {deskOpen && desk.teams.map(team => {
                    const tKey = teamKey(desk.name, team.name);
                    const teamOpen = expandedKeys.has(tKey);
                    return (
                    <React.Fragment key={`${desk.name}-${team.name}`}>
                      <tr onClick={() => toggleKey(tKey)} className={`cursor-pointer ${isDark ? 'bg-[#111827] hover:bg-[#1a2036]' : 'bg-white hover:bg-gray-50'}`}>
                        <td className={`${groupTd} pl-8`} colSpan={4}>
                          <span className="inline-flex items-center gap-1.5">
                            {teamOpen ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                            Team: {team.name}
                          </span>
                        </td>
                        <td className={`${groupTd} tabular-nums`}>{targetFmt(team.total)}</td>
                        <td className={td}></td>
                      </tr>
                      {teamOpen && team.users.map(user => (
                        <tr key={user.id} className={isDark ? 'hover:bg-[#1a1f3a]' : 'hover:bg-gray-50'}>
                          <td className={`${td} pl-12 font-medium ${isDark ? 'text-white' : 'text-gray-900'}`}>{user.name}</td>
                          <td className={td}>{user.role}</td>
                          <td className={td}>{user.desk || '-'}</td>
                          <td className={td}>{user.team || '-'}</td>
                          <td className={td}>
                            <input
                              type="number"
                              min="0"
                              step={department === 'sales' ? '1' : '100'}
                              value={targets[user.id]?.[field] ?? 0}
                              onChange={e => setTargets(prev => ({
                                ...prev,
                                [user.id]: { ...(prev[user.id] || {}), [field]: e.target.value },
                              }))}
                              className={input}
                            />
                          </td>
                          <td className={td}>
                            <button type="button" onClick={() => saveTarget(user)} disabled={savingId === user.id} className={button}>
                              {savingId === user.id ? 'Saving...' : 'Save'}
                            </button>
                          </td>
                        </tr>
                      ))}
                    </React.Fragment>
                    );
                  })}
                </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
